import React, { useState } from 'react';
import { SelectPicker } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORY_OPTIONS } from '../../utils/expenseCategories';
import { UI, Section, Note, List, Chapter } from '../shared/helpModalParts';

// A walkthrough of filing an expense, written against the actual controls rather
// than in the abstract — every bolded name below is a button, toggle or field
// that exists on the form, so the reader can match what they are told to what
// they are looking at. One continuous scroll rather than paged steps: it reads
// in the order the form is filled in, and Ctrl+F finds anything in it.

export default function ExpenseHelpModal({ open, onClose, fullScreen = false }) {
    const auth = useAuth();
    const canSubmitOnBehalf =
        typeof auth.hasPermission === 'function' && auth.hasPermission('submit_expenses_on_behalf');

    // Backs the sample Category dropdown below. Local to this modal and read by
    // nothing else, so opening it here can't touch a real expense.
    const [sampleCategory, setSampleCategory] = useState(null);

    const chapters = [
        {
            key: 'start',
            label: 'Start',
            icon: 'bi-plus-circle',
            body: (
                <>
                    <Section title="One expense, or a batch?">
                        Click <UI icon="bi-plus-lg">New Expense</UI> and pick one:
                        <List>
                            <li>
                                <UI icon="bi-receipt">Individual expense</UI> a single purchase, gets approved on its own.
                            </li>
                            <li>
                                <UI icon="bi-collection">Part of a group of expenses</UI> use this when submitting multiple related expenses.
                                Usually for a trip (you can submit different types of expenses in one group).
                            </li>
                        </List>
                    </Section>

                    <Section title="Starting a group">
                        Type a name under <UI>Name this group</UI> E.g. like "Seattle onsite - January" then click{' '}
                        <UI>Start group</UI>. Nothing in a group is sent for approval until you
                        submit the group.
                    </Section>

                    {canSubmitOnBehalf ? (
                        <Note title="Filing for someone else">
                            You can file on another person's behalf. For a single expense use the{' '}
                            <UI>Employee Name</UI> dropdown on the form; for a group choose under{' '}
                            <UI>Whose expenses are these?</UI> before starting it, since every expense
                            in a group belongs to the same person. The expense is owned by whoever you
                            pick, not by you.
                        </Note>
                    ) : null}
                </>
            ),
        },
        {
            key: 'details',
            label: 'Details',
            icon: 'bi-pencil-square',
            body: (
                <>
                    <Section title="Category">
                        Pick the associated category from the dropdown list.
                        <div style={{ marginTop: 10 }}>
                            <SelectPicker
                                data={CATEGORY_OPTIONS}
                                value={sampleCategory}
                                onChange={setSampleCategory}
                                placeholder="Select a category to continue"
                                cleanable={false}
                                searchable={false}
                                style={{ width: '100%' }}
                                // Matches the real field: the menu portals to the
                                // body so it can overhang the modal instead of
                                // being clipped by the scrolling body.
                                placement="bottomStart"
                                preventOverflow
                            />
                        </div>
                    </Section>

                    <Note title="All Hands Meeting meal">
                        <UI>Pre-Approved Meal Reimbursement for All Hands Meeting</UI> is a fixed amount
                        and needs no receipt. Pick it, choose the <UI>Meeting Date</UI>, and submit. It goes
                        straight to your approved expenses. It can't be added to a group.
                    </Note>

                    <Section title="Amount and dates">
                        <UI>Amount</UI> enter the amount to be reimbursed. If you are submitting for multiple meals, the amount should
                        be the sum of all the meals. You will be asked to upload receipts that total to this amount!
                    </Section>

                    <Section title="The four toggles">
                        <List>
                            <li>
                                <UI>Are there multiple purchase dates?</UI> select yes if these purchases were made on more than one date.
                                For example, grouping all meals for a trip over 4 days.
                            </li>
                            <li>
                                <UI>Was the company card used?</UI> RARE! Select yes if this was purchased with the NSI company card.
                            </li>
                            <li>
                                <UI>Customer Reimbursable?</UI> select yes if this expense will be directly reimbursable by the customer.
                                NSI will still reimburse you directly for this, but we need this info to request payment from the customer.
                            </li>
                            <li>
                                <UI>Travel?</UI> select yes if this expense involves travel of any sort. Most meals involve travel because
                                you have to drive there to get it. You should be submitting mileage as well even if it is a short trip.
                            </li>
                        </List>
                    </Section>

                    <Section title="Text input fields">
                        <List>
                            <li>
                                <UI>Vendor Name</UI> Only appears for Airfare, Ground Transportation, and Lodging. Only required for us to
                                audit the use of preferred vendors.
                            </li>
                            <li>
                                <UI>Description</UI> Describe what you bought, e.g. lunch for 2 days
                            </li>
                            <li>
                                <UI>Reason for Purchase</UI> Enter why you purchased, e.g. meal for onsite trip.
                            </li>
                        </List>
                    </Section>
                </>
            ),
        },
        {
            key: 'receipts',
            label: 'Receipts',
            icon: 'bi-paperclip',
            body: (
                <>
                    <Section title="Attach the files">
                        Under <UI>Attachments</UI>, drop files in or browse for them. On a phone you
                        can photograph the receipt directly.
                    </Section>

                    <Section title="Tag each file">
                        If you are uploading receipts with amounts on them, turn this on. You will then be prompted to enter the amount.
                        This helps you sum up multiple receipts and ensures that the sum of your receipts is correct.
                    </Section>

                    <Note tone="warn" title="Receipts are required">
                        Anything reimbursable needs the actual receipt. Credit card and bank
                        statements are not accepted.
                    </Note>

                    <Note title="Mileage">
                        Enter the miles driven and the amount is calculated for you at the IRS rate in
                        effect on the trip date. Screenshot your route map (Google Maps or similar)
                        showing the distance and upload that. It isn't a receipt, so leave{' '}
                        <UI>Is this attachment a receipt?</UI> off.
                    </Note>
                </>
            ),
        },
        {
            key: 'submit',
            label: 'Submit',
            icon: 'bi-send',
            body: (
                <>
                    <Section title="Send it">
                        <UI>Submit Expense</UI> sends it for approval. If the button is greyed out,
                        hover over it and the system will show you what is missing. 
                    </Section>

                    <Section title="Not finished yet?">
                        <UI>Save for later</UI> keeps it as a draft. Nothing is sent and no one
                        reviews it until you open it again and submit. Drafts are labelled{' '}
                        <UI>Not submitted</UI> in your list.
                    </Section>

                    <Section title="Inside a group">
                        The button reads <UI>Save Expense</UI> instead, because a group is sent as a
                        whole. After each save you're asked whether to{' '}
                        <UI icon="bi-plus-lg">Add another expense</UI> or{' '}
                        <UI>I'm done</UI>. When the batch is complete, open the group card and click{' '}
                        <UI>Submit all N expenses</UI>.
                    </Section>

                    <Section title="Where it goes">
                        Submitted expenses sit under <UI>Pending</UI> on this page until they're
                        decided, then move to <UI>Approved</UI> in the collapsed panel below it.
                    </Section>
                </>
            ),
        },
    ];

    return (
        <Modal open={open} onClose={onClose} size={fullScreen ? 'full' : 'lg'}>
            <Modal.Header>
                <Modal.Title>
                    <i className="bi bi-question-circle me-2" aria-hidden="true" />
                    How to submit an expense
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {chapters.map((chapter, i) => (
                    <Chapter
                        key={chapter.key}
                        title={chapter.label}
                        icon={chapter.icon}
                        first={i === 0}
                    >
                        {chapter.body}
                    </Chapter>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={onClose}>
                    Got it
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
